import React, { useEffect, useState, useRef } from "react";
import { navigate } from "gatsby";
import PropTypes from "prop-types";

import { addSearchClient } from "../../helpers/addsearch-client";
import SearchBar from "../search-bar/search-bar";
import SuggestionsList from "./suggestions-list";

/**
 * This component renders the search suggestions input with the search bar and a suggestions list.
 *
 *
 * @param {Object} props - The object containing props.
 * @param {"default" | "select-banking"} props.type - optional string to use a custom behavior when suggesting/searching.
 * @param {string} props.idSuffix - required string to append to the id of the search bar component.
 * @param {string} props.initialValue - optional to set the initial value of the search input.
 * @param {boolean} props.widgetBehavior - optional boolean value to show the search bar and suggestions inside a white container with a shadow.
 * @param {React.JSX.Element} props.LabelElement - required if widgetBehavior is true, we should set the label in here and not outside in the parent, optional React element to use as a label for the search input.
 * @param {Object} props.searchFormRef - optional React ref to be able to access the search value and suggestions list or more when needed.
 * @param {boolean} props.focusOnRender - if we want to focus the search input and start typing right away we set it to true.
 * @param {function} props.parentOnSearch - if we need to call a function in the parent component when the search is performed, we can pass it as a prop.
 * @param {function} props.parentOnSuggestionClick - if we need to call a function in the parent component when a suggestion is clicked, we can pass it as a prop.
 * @param {Array} props.CUSTOM_SUGGESTIONS - optional array of custom suggestions, if defined, we need to be sure there is a function that handles this data and sets the querySuggestions state.
 * 
 * @returns {React.JSX.Element} Returns the JSX component to render.
 */
const SearchSuggestionsInput = ({
  idSuffix = "",
  type = "default",
  initialValue = "",
  widgetBehavior = false,
  LabelElement = null,
  searchFormRef = null,
  focusOnRender = false,
  parentOnSearch = () => {},
  parentOnSuggestionClick = () => {},
  CUSTOM_SUGGESTIONS = []
}) => {
  const [shouldFocus, setShouldFocus] = useState(focusOnRender);
  const [searchFormFocused, setSearchFormFocused] = useState(false);
  const [query, setQuery] = useState(initialValue);
  const [search, setSearch] = useState(initialValue);
  const [querySuggestions, setQuerySuggestions] = useState([]);

  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const labelElementRef = useRef(null);

  const restructureSuggestionsArray = (suggestionsData, idKey, textKey) => {
    const restructuredArray = suggestionsData.map((suggestion) => ({
      id: suggestion[idKey],
      text: suggestion[textKey],
      data: suggestion
    }));

    return restructuredArray;
  }

  const addSearchCallback = (res) => {
    const results = res.hits.filter((element) => element.title != null);

    const restructuredArray = restructureSuggestionsArray(results, "id", "title");

    setQuerySuggestions(restructuredArray);
  };

  const searchSelectBankersSuggestions = (searchValue) => {
    let filteredBankers = CUSTOM_SUGGESTIONS.filter((banker) => banker.Name.toLowerCase().includes(searchValue.toLowerCase()));

    const restructuredArray = restructureSuggestionsArray(filteredBankers, "selectBankerId", "Name");

    setQuerySuggestions(restructuredArray);
  };

  useEffect(() => {
    // We don't need to set the analytics for the AddSearch Client, we are using it only for searching suggestions.
    if (type === "default") addSearchClient.setCollectAnalytics(false);
  }, []);

  const GET_SUGGESTIONS = {
    default: (newValue) => addSearchClient.performSearch(newValue, addSearchCallback),
    "select-banking": (newValue) => searchSelectBankersSuggestions(newValue)
  };

  const onInputChange = (newValue) => {
    setQuery(newValue);
    setSearchFormFocused(true);
    if (newValue) GET_SUGGESTIONS[type](newValue);
    else setQuerySuggestions([]);
  };

  const onSuggestionPopulate = (queryData) => {
    // console.log("Populating", queryData);
    const cleanedSuggestion = queryData.text.replaceAll(" | WaFd Bank", "");
    setQuery(cleanedSuggestion);
    GET_SUGGESTIONS[type](cleanedSuggestion);
  };

  const ON_PRESS_ENTER = {
    default: (newValue) => {
      navigate(`/customer-service/search?addsearch=${newValue}`);
      addSearchClient.statsSearchEvent(newValue);

      parentOnSearch(newValue);
    },
    "select-banking": (newValue) => {
      parentOnSearch(newValue);
    }
  };

  const onPressEnter = (newValue) => {
    if (newValue) {
      setSearchFormFocused(false);
      setSearch(newValue);
      ON_PRESS_ENTER[type](newValue);
    }
  };

  const ON_SEARCH_BUTTON_CLICK = {
    default: (currentQuery) => {
      addSearchClient.statsSearchEvent(currentQuery);
      navigate(`/customer-service/search?addsearch=${currentQuery}`);

      parentOnSearch(currentQuery);
    },
    "select-banking": (currentQuery) => {
      parentOnSearch(currentQuery);
    }
  };

  const onSearchButtonClick = (currentQuery) => {
    if (currentQuery !== search) {
      if (currentQuery !== "") {
        setSearch(currentQuery);
        setSearchFormFocused(false);
        ON_SEARCH_BUTTON_CLICK[type](currentQuery);
      }
    }
  };

  const ON_SUGGESTION_CLICK = {
    default: (suggestionData) => {
      const { id, text, index } = suggestionData;

      const clearedText = text.replaceAll(" | WaFd Bank", "");

      addSearchClient.performSearch(text);

      navigate(`/customer-service/search?addsearch=${clearedText}`);

      addSearchClient.statsSearchEvent(text);
      addSearchClient.statsSearchClick(text, {
        documentId: id,
        position: index
      });

      parentOnSuggestionClick(suggestionData);
    },
    "select-banking": (suggestionData) => {
      parentOnSuggestionClick(suggestionData);
    }
  };

  const onSuggestionClick = (suggestionData) => {
    const { text } = suggestionData;

    const clearedText = text.replaceAll(" | WaFd Bank", "");

    setQuery(clearedText);
    setSearch(clearedText);
    setQuerySuggestions([]);
    setSearchFormFocused(false);

    ON_SUGGESTION_CLICK[type](suggestionData);
  };

  useEffect(() => {
    if(widgetBehavior){
      const onClickHandler = (event) => {
        if (searchFormRef && searchFormRef.current) {
          if (!searchFormRef.current.contains(event.target)) {
            setSearchFormFocused(false);
          }
        }
      };
  
      window.addEventListener("click", onClickHandler);
      return () => window.removeEventListener("click", onClickHandler);
    }
  }, [searchFormRef]);

  useEffect(() => {
    if(widgetBehavior){
      if (typeof window !== "undefined" && labelElementRef.current) {
        // 40px (Search bar height) + (LabelElement height) + (1rem from "my-2" of the LabelElement)
        // make it negative to move the search form up.
        setPlaceholderHeight(
          40 +
            (labelElementRef.current.getBoundingClientRect().height +
              parseFloat(getComputedStyle(document.documentElement).fontSize)) +
            "px"
        );
      }
    }
  }, [labelElementRef.current]);

  const widgetClass = widgetBehavior ? (searchFormFocused ? "focused" : "") : "";
  const SHOW_PLACEHOLDER = widgetBehavior && searchFormFocused ? true : false;

  return (
    <div style={{ position: "relative" }}>
      {SHOW_PLACEHOLDER && <div className="ssi-placeholder" style={{ height: placeholderHeight }} />}
      <div
        ref={searchFormRef}
        className={"ssi-container " + widgetClass}
        style={widgetClass ? { paddingBottom: query ? "0px" : "1rem" } : {}}
      >
        {LabelElement && <LabelElement id="ssi-input-label" ref={labelElementRef} className="my-2 text-gray-60" style={{ paddingLeft: 3 }} />}
        <form autoComplete="off">
          <div className="d-flex w-100">
            <SearchBar
              id="ssi"
              idSuffix={idSuffix}
              query={query}
              label="Search WaFd Bank"
              customSearchIconElement={type === "select-banking" && <p className="mb-0 fw-bold">Go</p>}
              onInputChange={(event) => onInputChange(event.target.value)}
              onPressEnter={onPressEnter}
              onSearchButtonClick={onSearchButtonClick}
              shouldFocus={shouldFocus}
              shouldFocusCallback={() => setShouldFocus(false)}
            />
          </div>
        </form>
        <SuggestionsList
          query={query}
          data={querySuggestions}
          searchFormFocused={searchFormFocused}
          onSuggestionPopulate={onSuggestionPopulate}
          onSuggestionClick={onSuggestionClick}
        />
      </div>
    </div>
  );
};

export default SearchSuggestionsInput;

SearchSuggestionsInput.propTypes = {
  idSuffix: PropTypes.string.isRequired,
  type: PropTypes.string,
  initialValue: PropTypes.string,
  widgetBehavior: PropTypes.bool,
  LabelElement: PropTypes.node,
  searchFormRef: PropTypes.object,
  focusOnRender: PropTypes.bool,
  parentOnSearch: PropTypes.func,
  parentOnSuggestionClick: PropTypes.func,
  CUSTOM_SUGGESTIONS: PropTypes.arrayOf(PropTypes.object),
};

